// src/portal/portalRollup.js
//
// The rollup card's four cells and its percentage, derived from route 5's
// counts. Pure, so the arithmetic can be tested without rendering anything.

/**
 * ── THE DENOMINATOR IS NOT `total`, AND THAT IS THE DECISION ────────────────
 *
 * approved / submitted_ever, where submitted_ever is everything that has been
 * offered to a counterparty at least once.
 *
 * A draft has never been shown to anyone. Counting it against the percentage
 * reads as a backlog the reviewer does not have and cannot act on -- their own
 * queue would drag their own number down. The server computes the denominator
 * as `status <> 'draft'` for the same reason.
 *
 * ZERO IS NOT 0%. With nothing submitted there is no ratio to report, and 0%
 * reads as failure rather than as absence. `null` means "no figure yet" and the
 * card shows a dash.
 *
 * @param {object} counts route 5's `counts`
 * @returns {{pct:number|null, approved:number, awaitingYou:number, inRevision:number, plans:number}}
 */
export function rollupFrom(counts) {
  const c = counts ?? {};
  const approved = c.approved ?? 0;
  // Fall back to total only if the server is older than this field; a missing
  // denominator must not silently become `total` on a payload that has drafts.
  const denom = c.submitted_ever ?? null;
  return {
    pct: denom ? Math.round((approved / denom) * 100) : null,
    approved,
    awaitingYou: c.awaiting_you ?? 0,
    inRevision: c.changes_requested ?? 0,
    plans: c.total ?? 0,
  };
}

/**
 * The four cells, in the mock's order, with the value that gets the accent.
 *
 * `tone` names a role rather than a colour: 'due' is the one you must act on,
 * 'ok' is the settled one. The component maps those to tokens.
 */
export function rollupCells(r) {
  return [
    { label: "Awaiting you", value: r.awaitingYou, tone: r.awaitingYou > 0 ? "due" : "idle" },
    { label: "In revision",  value: r.inRevision,  tone: "idle" },
    { label: "Approved",     value: r.approved,    tone: "ok" },
    { label: "Plans",        value: r.plans,       tone: "idle" },
  ];
}
